"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useEffectEvent, useRef, useState, type ReactNode } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useAnalytics, getEvent } from "@/lib/analytics";
import { railFloatLeft, useSidebar } from "@/entities/sidebar";
import { cn } from "@/lib/utils";
import { useAnnouncementStore } from "../model/use-announcement-store";
import { FeedbackForm } from "./feedback-form";
import { FeedbackPanelAutoOpenContext, FeedbackPanelExpansionContext, FeedbackPanelHeaderContext } from "./feedback-panel-context";
import { FeedbackTabSwitch } from "./feedback-tab-switch";
import { GlowIconContainer } from "@/shared/ui/glass";

// ============================================================================
// Component
// ============================================================================

interface FeedbackModalProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly featureKey?: string | null;
  readonly yourFeedbackContent?: React.ReactNode;
  /** Unread replies awaiting the user — same count the Feedback button badges. */
  readonly feedbackUnreadCount?: number;
}
export function FeedbackModal(t0) {
  const $ = _c(38);
  const {
    isOpen,
    onClose,
    featureKey,
    yourFeedbackContent,
    feedbackUnreadCount: t1
  } = t0;
  const feedbackUnreadCount = t1 === undefined ? 0 : t1;
  const {
    track
  } = useAnalytics();
  const [tab, setTab] = useState("give");
  const [panelWantsExpand, setPanelWantsExpand] = useState(false);
  const expanded = tab === "yours" && panelWantsExpand;
  const [panelHeader, setPanelHeader] = useState(null);
  const headerSlot = tab === "yours" ? panelHeader : null;
  const [yoursEverOpened, setYoursEverOpened] = useState(false);
  if (tab === "yours" && !yoursEverOpened) {
    setYoursEverOpened(true);
  }
  const [autoOpen, setAutoOpen] = useState(false);
  const prevIsOpenForTab = useRef(isOpen);
  const [wasOpen, setWasOpen] = useState(isOpen);
  if (isOpen !== wasOpen) {
    setWasOpen(isOpen);
    if (!isOpen) {
      setAutoOpen(false);
    } else {
      if (feedbackUnreadCount === 0) {
        setTab("give");
      }
    }
  }
  const cardRef = useRef(null);
  const [giveHeight, setGiveHeight] = useState(null);
  const unreadCountRef = useRef(feedbackUnreadCount);
  let t2;
  let t3;
  if ($[0] !== feedbackUnreadCount) {
    t2 = () => {
      unreadCountRef.current = feedbackUnreadCount;
    };
    t3 = [feedbackUnreadCount];
    $[0] = feedbackUnreadCount;
    $[1] = t2;
    $[2] = t3;
  } else {
    t2 = $[1];
    t3 = $[2];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[3] !== isOpen) {
    t4 = () => {
      const justOpened = isOpen && !prevIsOpenForTab.current;
      prevIsOpenForTab.current = isOpen;
      if (!justOpened || unreadCountRef.current === 0) {
        return;
      }
      const frame = requestAnimationFrame(() => {
        const measured = cardRef.current?.offsetHeight ?? 0;
        if (measured > 0) {
          setGiveHeight(prev => prev ?? measured);
        }
        setTab("yours");
        setAutoOpen(true);
      });
      return () => cancelAnimationFrame(frame);
    };
    t5 = [isOpen];
    $[3] = isOpen;
    $[4] = t4;
    $[5] = t5;
  } else {
    t4 = $[4];
    t5 = $[5];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[6] !== isOpen || $[7] !== tab) {
    t6 = () => {
      if (!isOpen || tab !== "give") {
        return;
      }
      const el = cardRef.current;
      if (!el) {
        return;
      }
      const ro = new ResizeObserver(entries => {
        const entry = entries[0];
        if (!entry) {
          return;
        }
        setGiveHeight(entry.borderBoxSize?.[0]?.blockSize ?? entry.contentRect.height);
      });
      ro.observe(el);
      return () => ro.disconnect();
    };
    t7 = [isOpen, tab];
    $[6] = isOpen;
    $[7] = tab;
    $[8] = t6;
    $[9] = t7;
  } else {
    t6 = $[8];
    t7 = $[9];
  }
  useEffect(t6, t7);
  const isCapturingScreen = useAnnouncementStore(_temp);
  const isChatHistoryOpen = useAnnouncementStore(_temp2);
  const chatHistoryWidth = useAnnouncementStore(_temp3);
  const shouldHideForHistory = isChatHistoryOpen;
  const isSidebarCollapsed = useSidebar(_temp4);
  const railOffsetLeft = railFloatLeft(isSidebarCollapsed);
  const modalLeft = shouldHideForHistory ? railOffsetLeft + chatHistoryWidth - 10 : railOffsetLeft;
  let t8;
  if ($[10] !== featureKey || $[11] !== track) {
    t8 = () => {
      track(getEvent("feature-announcement", "feedback_sidebar_opened"), {
        featureKey: featureKey ?? null,
        displayMode: "modal"
      });
    };
    $[10] = featureKey;
    $[11] = track;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  const trackOpened = useEffectEvent(t8);
  let t9;
  if ($[13] !== isOpen || $[14] !== trackOpened) {
    t9 = () => {
      if (isOpen) {
        trackOpened();
      }
    };
    $[13] = isOpen;
    $[14] = trackOpened;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== isOpen) {
    t10 = [isOpen];
    $[16] = isOpen;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  useEffect(t9, t10);
  let t11;
  let t12;
  if ($[18] !== isOpen || $[19] !== onClose) {
    t11 = () => {
      if (!isOpen) {
        return;
      }
      const handleKey = function handleKey(e) {
        if (e.key === "Escape") {
          onClose();
        }
      };
      window.addEventListener("keydown", handleKey);
      return () => window.removeEventListener("keydown", handleKey);
    };
    t12 = [isOpen, onClose];
    $[18] = isOpen;
    $[19] = onClose;
    $[20] = t11;
    $[21] = t12;
  } else {
    t11 = $[20];
    t12 = $[21];
  }
  useEffect(t11, t12);
  let t13;
  if ($[22] !== autoOpen || $[23] !== expanded || $[24] !== featureKey || $[25] !== feedbackUnreadCount || $[26] !== giveHeight || $[27] !== headerSlot || $[28] !== isCapturingScreen || $[29] !== isOpen || $[30] !== modalLeft || $[31] !== onClose || $[32] !== tab || $[33] !== yourFeedbackContent || $[34] !== yoursEverOpened) {
    t13 = isOpen && <motion.div ref={cardRef} role="dialog" aria-label="Send feedback" className={cn("fixed bottom-6 z-1500 flex max-[480px]:bottom-0 max-[480px]:left-0 max-[480px]:w-full flex-col rounded-2xl max-[480px]:rounded-t-2xl max-[480px]:rounded-b-none bg-white shadow-xl transition-[left,width,height] duration-300 ease-out", expanded ? "w-[780px] min-[481px]:max-w-[calc(100vw-23rem)]" : "w-[380px]", "left-(--feedback-modal-left)")} style={{
      maxHeight: "calc(100dvh - 3rem)",
      height: tab === "yours" && giveHeight != null ? giveHeight : undefined,
      visibility: isCapturingScreen ? "hidden" : undefined,
      "--feedback-modal-left": `${modalLeft}px`
    } as React.CSSProperties} initial={{
      opacity: 0,
      scale: 0.95,
      transformOrigin: "bottom left"
    }} animate={{
      opacity: 1,
      scale: 1
    }} exit={{
      opacity: 0,
      scale: 0.95
    }} transition={{
      type: "spring",
      damping: 25,
      stiffness: 300
    }}><div className="flex items-center gap-2 border-b border-gray-100 px-4 py-3"><GlowIconContainer glow="brand" className="h-7 w-7 rounded-md"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="text-cova-brand"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg></GlowIconContainer><h2 className={cn("text-sm font-semibold text-gray-900", headerSlot && "max-[480px]:hidden")}>Feedback</h2>{headerSlot && <div className="flex min-w-0 flex-1 items-center gap-2">{headerSlot}</div>}<button type="button" onClick={onClose} aria-label="Close feedback" className="ml-auto flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"><svg width="12" height="12" viewBox="0 0 14 14" fill="none" aria-hidden="true"><path d="M1 1L13 13M1 13L13 1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg></button></div>{yourFeedbackContent && <FeedbackTabSwitch tab={tab} onChange={setTab} unreadCount={feedbackUnreadCount} className="px-3" />}<div className={cn(yourFeedbackContent && tab === "yours" ? "hidden" : "contents")}><FeedbackForm featureKey={featureKey} variant="modal" isOpen={isOpen} onSuccess={onClose} /></div>{yourFeedbackContent && yoursEverOpened && <FeedbackPanelExpansionContext.Provider value={setPanelWantsExpand}><FeedbackPanelHeaderContext.Provider value={setPanelHeader}><FeedbackPanelAutoOpenContext.Provider value={autoOpen}><div className={cn("min-h-0 overflow-hidden", tab === "yours" ? "flex-1" : "hidden")}>{yourFeedbackContent}</div></FeedbackPanelAutoOpenContext.Provider></FeedbackPanelHeaderContext.Provider></FeedbackPanelExpansionContext.Provider>}</motion.div>;
    $[22] = autoOpen;
    $[23] = expanded;
    $[24] = featureKey;
    $[25] = feedbackUnreadCount;
    $[26] = giveHeight;
    $[27] = headerSlot;
    $[28] = isCapturingScreen;
    $[29] = isOpen;
    $[30] = modalLeft;
    $[31] = onClose;
    $[32] = tab;
    $[33] = yourFeedbackContent;
    $[34] = yoursEverOpened;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  let t14;
  if ($[36] !== t13) {
    t14 = <AnimatePresence>{t13}</AnimatePresence>;
    $[36] = t13;
    $[37] = t14;
  } else {
    t14 = $[37];
  }
  return t14;
}
function _temp4(s_2) {
  return s_2.isCollapsed;
}
function _temp3(s_1) {
  return s_1.chatHistoryWidth;
}
function _temp2(s_0) {
  return s_0.isChatHistoryOpen;
}
function _temp(s) {
  return s.isCapturingScreen;
}